import { api, unwrap } from './client';
import type { components } from '../types/learnerApi';
import type { IssuedCertificate } from '../types/learnerCertificates';
import type { MemberConfigEntry, MemberConfigUpsert } from '../types/memberConfig';
import type {
  LearnerPrize,
  LearnerPrizeClaim,
  LearnerPrizeDetail,
  LeaderboardEntry,
  LeaderboardQueryParams,
  Paginated,
  PaginationMeta,
  PrizeClaimsQueryParams,
} from '../types/learnerPrizes';

type LearnerProfile = components['schemas']['LearnerProfile'];
type Dashboard = components['schemas']['Dashboard'];
type CourseBuckets = components['schemas']['CourseBuckets'];
type CourseDetail = components['schemas']['CourseDetail'];
type GamificationBlock = components['schemas']['GamificationBlock'];
type Program = components['schemas']['Program'];
type LearnerProgress = components['schemas']['LearnerProgress'];

export async function getProfile(): Promise<LearnerProfile> {
  const res = await api.get('/me/profile');
  return unwrap(res);
}

export async function getDashboard(): Promise<Dashboard> {
  const res = await api.get('/me/dashboard');
  return unwrap(res);
}

export async function getCourses(): Promise<CourseBuckets> {
  const res = await api.get('/me/courses');
  return unwrap(res);
}

export async function getCourse(assignmentId: string): Promise<CourseDetail> {
  const res = await api.get(`/me/courses/${assignmentId}`);
  return unwrap(res);
}

export async function getPoints(): Promise<GamificationBlock> {
  const res = await api.get('/me/gamification/points');
  return unwrap(res);
}

export async function getPrograms(): Promise<Program[]> {
  const res = await api.get('/me/programs');
  return unwrap(res);
}

export async function getCertificates(): Promise<IssuedCertificate[]> {
  const res = await api.get('/me/certificates');
  return unwrap(res);
}

// ─── Member config (per-membership key/value; e.g. dismissed reminders) ──────

/** All of the caller's config entries. */
export async function getConfig(): Promise<MemberConfigEntry[]> {
  const res = await api.get('/me/config');
  return unwrap(res);
}

/**
 * Create or update one config entry. Partial — `value`/`payload` are written
 * only when present; `enabled` defaults to `true` server-side.
 */
export async function upsertConfig(body: MemberConfigUpsert): Promise<MemberConfigEntry> {
  const res = await api.put('/me/config', body);
  return unwrap(res);
}

// ─── Legal acknowledgement ─────────────────────────────────────────────────

/** Proof-of-acknowledgement echo returned by `/me/legal-acknowledgement`. */
export interface LegalAcknowledgement {
  acknowledged_at: string;
  terms_version: string;
  privacy_version: string;
}

/**
 * Record that the caller acknowledged the current Terms & Conditions and
 * Privacy Policy. Used by invited members, who never see the signup
 * questionnaire.
 *
 * Idempotent: an existing acknowledgement for the current terms version comes
 * back as 200 with that record instead of creating a second one.
 */
export async function acknowledgeLegal(): Promise<LegalAcknowledgement> {
  const res = await api.post('/me/legal-acknowledgement');
  return unwrap(res);
}

// ─── Course content lifecycle ──────────────────────────────────────────────

/**
 * Mark a content-type module (rich_text, image_embed, video_url_embed,
 * pdf_file, source_code_file, scorm_zip, video_meeting_link) complete.
 * Idempotent — already-completed modules return the existing progress row.
 * 409 WRONG_MODULE_TYPE for evaluation / feedback (use those routes instead).
 *
 * `durationSeconds` (optional) — how long the learner spent on the module from
 * open until clicking complete. Sent as `{ duration_seconds }` in the body so
 * the backend can record `time_spent_seconds` and emit a `module_completed`
 * analytics event. Backend ignores `0`, negative, or absent values.
 */
export async function markModuleComplete(
  assignmentId: string,
  moduleId: string,
  durationSeconds?: number
): Promise<LearnerProgress> {
  const body =
    typeof durationSeconds === 'number' && durationSeconds > 0
      ? { duration_seconds: durationSeconds }
      : undefined;
  const res = await api.post(
    `/me/courses/${assignmentId}/modules/${moduleId}/complete`,
    body
  );
  return unwrap(res);
}

/**
 * Build the close-on-leave beacon URL. The actual call is dispatched via
 * `navigator.sendBeacon` from a `pagehide` listener so it survives page
 * teardown — this helper keeps the URL in one place.
 */
export function leaveBeaconUrl(assignmentId: string): string {
  // Match the configured base URL prefix.
  const base = (api.defaults.baseURL ?? '').replace(/\/$/, '');
  return `${base}/me/courses/${assignmentId}/leave`;
}

/** Programmatic close-on-leave (used by tests / non-beacon callers). */
export async function closeCourseOnLeave(assignmentId: string): Promise<{ reset: number }> {
  const res = await api.post(`/me/courses/${assignmentId}/leave`);
  return unwrap(res);
}

/**
 * Per-call cap for a single dwell window (mirrors the backend
 * `TIME_SPENT_CAP_SECONDS`). The client clamps to this so a long idle/background
 * window records the capped value instead of being rejected (400) and lost.
 */
export const MODULE_TIME_CAP_SECONDS = 3600;

/** Clamp a raw elapsed value to the integer 0..cap range the API accepts. */
export const clampModuleTime = (seconds: number): number =>
  Math.min(Math.max(Math.round(seconds), 0), MODULE_TIME_CAP_SECONDS);

/**
 * Record dwell time on a module (any type) when the learner navigates away,
 * without completing it. Additive server-side; fire-and-forget. No-ops for a
 * non-positive duration; clamps to the per-call cap. Used by `useModuleTimer`
 * on in-app module transitions.
 */
export async function recordModuleTime(
  assignmentId: string,
  moduleId: string,
  seconds: number
): Promise<void> {
  const duration = clampModuleTime(seconds);
  if (duration <= 0) return;
  await api.post(`/me/courses/${assignmentId}/modules/${moduleId}/time`, {
    duration_seconds: duration,
  });
}

/**
 * Beacon variant of {@link recordModuleTime} for hard unload (tab close /
 * refresh / background). Uses `navigator.sendBeacon` so it survives page
 * teardown; the server resolves tenant/membership from the auth cookie since a
 * beacon cannot set `X-Tenant-ID`. Best-effort — server idle timeouts backstop.
 */
export function recordModuleTimeBeacon(
  assignmentId: string,
  moduleId: string,
  seconds: number
): void {
  const duration = clampModuleTime(seconds);
  if (duration <= 0) return;
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;
  const base = (api.defaults.baseURL ?? '').replace(/\/$/, '');
  const url = `${base}/me/courses/${assignmentId}/modules/${moduleId}/time`;
  const blob = new Blob([JSON.stringify({ duration_seconds: duration })], {
    type: 'application/json',
  });
  try {
    navigator.sendBeacon(url, blob);
  } catch {
    // best-effort — a dropped beacon is acceptable; server timeouts backstop.
  }
}

// ─── Prizes ────────────────────────────────────────────────────────────────

export async function getPrizes(): Promise<LearnerPrize[]> {
  const res = await api.get('/me/prizes');
  return unwrap(res);
}

export async function getPrize(prizeId: string): Promise<LearnerPrizeDetail> {
  const res = await api.get(`/me/prizes/${prizeId}`);
  return unwrap(res);
}

export async function claimPrize(prizeId: string): Promise<LearnerPrizeClaim> {
  const res = await api.post(`/me/prizes/${prizeId}/claim`);
  return unwrap(res);
}

export async function markClaimReceived(claimId: string): Promise<LearnerPrizeClaim> {
  const res = await api.post(`/me/prizes/claims/${claimId}/received`);
  return unwrap(res);
}

export async function getPrizeClaims(
  params: PrizeClaimsQueryParams = {}
): Promise<Paginated<LearnerPrizeClaim>> {
  const res = await api.get('/me/prizes/claims', { params });
  return {
    data: (res.data?.data ?? []) as LearnerPrizeClaim[],
    meta: (res.data?.meta ?? {
      page: 1,
      per_page: params.per_page ?? 20,
      total: 0,
      pages: 0,
    }) as PaginationMeta,
  };
}

// ─── Gamification Leaderboard ──────────────────────────────────────────────

export async function getLeaderboard(
  params: LeaderboardQueryParams = {}
): Promise<Paginated<LeaderboardEntry>> {
  const res = await api.get('/me/gamification/leaderboard', { params });
  return {
    data: (res.data?.data ?? []) as LeaderboardEntry[],
    meta: (res.data?.meta ?? {
      page: 1,
      per_page: params.per_page ?? 20,
      total: 0,
      pages: 0,
    }) as PaginationMeta,
  };
}
